import { cookies } from "next/headers"
import { NextResponse } from "next/server"
import { z } from "zod"
import { getSession } from "@/services/auth-service"
import {
  applyCartTokenCookie,
  applyWooCartSessionCookies,
  applyWordPressCartCoupon,
  buildForwardCookieHeader,
  readWooGuestSessionCookies,
  removeWordPressCartCoupon,
} from "@/services/cart-service"

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-store",
} as const

const couponPayloadSchema = z.object({
  code: z.string().trim().min(1),
})

const getForwardedCookieHeader = async (): Promise<string> => {
  const session = await getSession()
  const cookieStore = await cookies()

  return buildForwardCookieHeader({
    authSessionToken: session?.wordpressSessionCookie ?? null,
    wooGuestSessionCookies: readWooGuestSessionCookies(cookieStore),
  })
}

export const POST = async (request: Request) => {
  const forwardedCookieHeader = await getForwardedCookieHeader()
  const payload = await request.json().catch(() => null)
  const parsedPayload = couponPayloadSchema.safeParse(payload)

  if (!parsedPayload.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PAYLOAD",
        message: "Payload invalido para aplicar cupom.",
      },
      { status: 422, headers: NO_CACHE_HEADERS }
    )
  }

  try {
    const result = await applyWordPressCartCoupon(
      parsedPayload.data,
      forwardedCookieHeader
    )

    const response = NextResponse.json(
      {
        ok: result.ok,
        message: result.message,
      },
      { headers: NO_CACHE_HEADERS }
    )

    applyWooCartSessionCookies(response, result.upstreamSetCookie)
    applyCartTokenCookie(response, result.cartToken)

    return response
  } catch {
    return NextResponse.json(
      {
        ok: false,
        code: "COUPON_APPLY_FAILED",
        message: "Nao foi possivel aplicar o cupom.",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}

export const DELETE = async (request: Request) => {
  const forwardedCookieHeader = await getForwardedCookieHeader()
  const payload = await request.json().catch(() => null)
  const parsedPayload = couponPayloadSchema.safeParse(payload)

  if (!parsedPayload.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PAYLOAD",
        message: "Payload invalido para remover cupom.",
      },
      { status: 422, headers: NO_CACHE_HEADERS }
    )
  }

  try {
    const result = await removeWordPressCartCoupon(
      parsedPayload.data,
      forwardedCookieHeader
    )

    const response = NextResponse.json(
      {
        ok: result.ok,
        message: result.message,
      },
      { headers: NO_CACHE_HEADERS }
    )

    applyWooCartSessionCookies(response, result.upstreamSetCookie)
    applyCartTokenCookie(response, result.cartToken)

    return response
  } catch {
    return NextResponse.json(
      {
        ok: false,
        code: "COUPON_REMOVE_FAILED",
        message: "Nao foi possivel remover o cupom.",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}
